import AttrValue, { AttrValueOptions } from 'models/AttrValue';
import Classification, { ClassificationOptions } from 'models/Classification';
import Group, { GroupOptions } from 'models/Group';
import User, { UserOptions } from 'models/User';

import GroupType, { GroupTypeOptions } from './GroupType';

export enum NotificationChannel {
  EMAIL = 'email',
  IN_APP = 'inApp',
  PUSH = 'push',
}

export type NotificationChannels = {
  [key in NotificationChannel]: boolean;
};

export interface NotificationsSelections {
  activity: Record<string, NotificationChannels>;
  criticalAlerts: Record<string, NotificationChannels>;
  patientStatusChanges: Record<string, NotificationChannels>;
}

export interface NotificationOptions {
  id: string;
  userId: string;
  clientId: string;
  active: boolean;
  groupTypeApiName: string;
  notificationSelections: NotificationsSelections;
  createdAt: Date;
  updatedAt: Date;
  type: string;
  groupType: Partial<GroupTypeOptions>;
  scopes: Partial<{
    episodes: Partial<ClassificationOptions>[];
    groups: Partial<GroupOptions>[];
    planTypes: Partial<ClassificationOptions>[];
    caseManagers: Partial<UserOptions>[];
    utilizationManagers: Partial<UserOptions>[];
    physicianTeams: Partial<AttrValueOptions>[];
  }>;
}

const getDefaults = (): NotificationOptions => ({
  id: '',
  userId: '',
  clientId: '',
  active: false,
  groupTypeApiName: '',
  notificationSelections: {
    activity: {},
    criticalAlerts: {},
    patientStatusChanges: {},
  },
  createdAt: new Date(),
  updatedAt: new Date(),
  type: '',
  groupType: {},
  scopes: {
    episodes: [],
    groups: [],
    planTypes: [],
    caseManagers: [],
    utilizationManagers: [],
    physicianTeams: [],
  },
});

// The inbound-referrals sister row (OLIO-3319/3322): one per (user, client) — no group-type
// discriminator, no tabs. Sections on the page are categories in the stored map, exactly like
// care coordination's; scopes narrow by referral-source display names (plain strings — the
// stored value IS the display name) and by group ids ("Specific SNFs", hydrated by the api).
export interface InboundReferralPreferenceOptions {
  id: string;
  userId: string;
  clientId: string;
  active: boolean;
  type: string;
  notificationSelections: Record<string, Record<string, Partial<NotificationChannels>> & { active?: boolean }>;
  scopes: Partial<{
    referralSources: string[];
    groups: Partial<GroupOptions>[];
  }>;
}

const getInboundReferralDefaults = (): InboundReferralPreferenceOptions => ({
  id: '',
  userId: '',
  clientId: '',
  active: false,
  type: '',
  notificationSelections: {},
  scopes: {
    referralSources: [],
    groups: [],
  },
});

export class InboundReferralPreference {
  id: string;
  userId: string;
  clientId: string;
  active: boolean;
  type: string;
  notificationSelections: Record<string, Record<string, Partial<NotificationChannels>> & { active?: boolean }>;
  scopes: {
    referralSources: string[];
    groups: Group[];
  };

  constructor(options: Partial<InboundReferralPreferenceOptions> = {}) {
    const opts = { ...getInboundReferralDefaults(), ...options };

    this.id = opts.id;
    this.userId = opts.userId;
    this.clientId = opts.clientId;
    this.active = opts.active;
    this.type = opts.type;
    this.notificationSelections = opts.notificationSelections;
    this.scopes = {
      referralSources: opts.scopes?.referralSources ?? [],
      groups: (opts.scopes?.groups ?? []).map((group) => new Group(group)),
    };
  }
}

// The user's own preferences, not tied to a product or a facility type: being tagged in a note.
// One per (user, client), like the inbound-referrals row, and no scopes.
export interface GeneralPreferenceOptions {
  id: string;
  userId: string;
  clientId: string;
  active: boolean;
  type: string;
  notificationSelections: Record<string, Record<string, Partial<NotificationChannels>> & { active?: boolean }>;
}

export class GeneralPreference {
  id: string;
  userId: string;
  clientId: string;
  active: boolean;
  type: string;
  notificationSelections: GeneralPreferenceOptions['notificationSelections'];

  constructor(options: Partial<GeneralPreferenceOptions> = {}) {
    this.id = options.id ?? '';
    this.userId = options.userId ?? '';
    this.clientId = options.clientId ?? '';
    this.active = options.active ?? false;
    this.type = options.type ?? '';
    this.notificationSelections = options.notificationSelections ?? {};
  }
}

export class Notification {
  id: string;
  userId: string;
  clientId: string;
  active: boolean;
  groupTypeApiName: string;
  notificationSelections: NotificationsSelections;
  createdAt: Date;
  updatedAt: Date;
  type: string;
  groupType: GroupType;
  scopes: Partial<{
    episodes: Classification[];
    groups: Group[];
    planTypes: Classification[];
    caseManagers: User[];
    utilizationManagers: User[];
    physicianTeams: AttrValue[];
  }>;

  constructor(options: Partial<NotificationOptions> = {}) {
    const opts = { ...getDefaults(), ...options };

    this.id = opts.id;
    this.userId = opts.userId;
    this.clientId = opts.clientId;
    this.active = opts.active;
    this.groupTypeApiName = opts.groupTypeApiName;
    this.notificationSelections = opts.notificationSelections;
    this.createdAt = opts.createdAt;
    this.updatedAt = opts.updatedAt;
    this.type = opts.type;
    this.groupType = new GroupType(opts.groupType);
    this.scopes = {
      episodes: opts.scopes?.episodes?.map((episode) => new Classification(episode)),
      groups: opts.scopes?.groups?.map((group) => new Group(group)),
      planTypes: opts.scopes?.planTypes?.map((planType) => new Classification(planType)),
      caseManagers: opts.scopes?.caseManagers?.map((caseManager) => new User(caseManager)),
      utilizationManagers: opts.scopes?.utilizationManagers?.map((utilizationManager) => new User(utilizationManager)),
      physicianTeams: opts.scopes?.physicianTeams?.map((physician) => new AttrValue(physician)),
    };
  }
}
